import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';

const VideoForm = forwardRef(({
  videoTime,
  setVideoTime,
  videoTitle,
  setVideoTitle,
  videoStatus,
  setVideoStatus,
  videoSequence,
  setVideoSequence,
  isLoading,
  error,
  selectedSubTopic,
  videoDetails,
  handleVideoStatus
}, ref) => {
  const [statusCheckResult, setStatusCheckResult] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [videoFile, setVideoFile] = useState(null);

  // Expose clearFileInput function to parent component
  useImperativeHandle(ref, () => ({
    clearFileInput: () => {
      const fileInput = document.getElementById('videoFile');
      if (fileInput) {
        fileInput.value = '';
      }
      setVideoFile(null);
      setUploadStatus('');
    }
  }));

  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    setVideoFile(file);
  };

  const handleStatusButtonClick = async () => {
    try {
      const result = await handleVideoStatus(videoDetails.video_id);
      setStatusCheckResult(result);
    } catch (error) {
      setStatusCheckResult({ status: 'error' });
    }
  };

  // Check if video is ready (has OTP and playback info)
  const isVideoReady = videoDetails && 
    videoDetails.video_otp && 
    videoDetails.video_otp !== "" && 
    videoDetails.video_playback_info && 
    videoDetails.video_playback_info !== "";

  // Reset status check result when video becomes ready
  useEffect(() => {
    if (isVideoReady) {
      setStatusCheckResult({ status: 'ready' });
    }
  }, [videoDetails?.video_otp, videoDetails?.video_playback_info]);

  // Clear video state when question/video changes
  useEffect(() => {
    setStatusCheckResult(null);
    setVideoFile(null);
    setUploadStatus('');
    const fileInput = document.getElementById('videoFile');
    if (fileInput) fileInput.value = '';
  }, [videoDetails?.video_id]);

  return (
    <div className="container-fluid p-1">
      <form className="shadow px-2 py-2 rounded border" style={{backgroundColor: '#f7f7f766'}}>
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {error}
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => {}}
            ></button>
          </div>
        )}
        
        {uploadStatus && (
          <div className={`alert ${uploadStatus.includes('Error') ? 'alert-danger' : 'alert-info'}`}>
            {uploadStatus}
          </div>
        )}

        {/* Video File Upload */}
        <div className="mb-3">
          <label htmlFor="videoFile" className="form-label">
            Choose Video File: <span className="text-danger">*</span>
          </label>
          <input
            type="file"
            id="videoFile"
            className="form-control custom-input"
            accept="video/*"
            onChange={handleVideoFileChange}
            required
          />
        </div>

        
        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label htmlFor="videoTime" className="form-label">
                Time (minutes): <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                id="videoTime"
                className="form-control custom-input"
                value={videoTime}
                onChange={(e) => setVideoTime(e.target.value)}
                placeholder="Enter time in minutes (e.g., 10)"
                min="0"
                required
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label htmlFor="videoSequence" className="form-label">
                Sequence: <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                id="videoSequence"
                className="form-control custom-input"
                value={videoSequence}
                onChange={(e) => setVideoSequence(e.target.value)}
                placeholder="Enter sequence number"
                min="1"
                required
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label htmlFor="videoTitle" className="form-label">
                Video Title: <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="videoTitle"
                className="form-control custom-input"
                value={videoTitle}
                onChange={(e) => setVideoTitle(e.target.value)}
                placeholder="Enter video title"
                required
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label htmlFor="videoStatus" className="form-label">
                Status: <span className="text-danger">*</span>
              </label>
              <select
                id="videoStatus"
                className="form-control custom-input"
                value={videoStatus}
                onChange={(e) => setVideoStatus(e.target.value)}
                required
              >
                <option value="active">Active</option>
                <option value="not active">Not Active</option>
              </select>
            </div>
          </div>
        </div>
        
        {/* Video Status Button - Only show when video is not ready */}
        {videoDetails && videoDetails.video_id && !isVideoReady && (
          <div className="row mt-3">
            <div className="col-12">
              <div className={`alert ${statusCheckResult?.status === 'ready' ? 'alert-success' : statusCheckResult?.status === 'error' ? 'alert-danger' : statusCheckResult?.status === 'Queued' ? 'alert-warning' : 'alert-warning'}`} role="alert">
                <strong>
                  {statusCheckResult?.status === 'ready' ? 'Video Ready!' : 
                   statusCheckResult?.status === 'Processing' ? 'Video Processing...' :
                   statusCheckResult?.status === 'Queued' ? 'Video Queued!' :
                   statusCheckResult?.status === 'error' ? 'Video Error' : 'Video Status Check:'}
                </strong>
                {statusCheckResult?.status === 'ready' ? ' Video is ready to play.' :
                 statusCheckResult?.status === 'Processing' ? ' Video is still being processed.' :
                 statusCheckResult?.status === 'Queued' ? ' Video is Queued for Processing.' :
                 statusCheckResult?.status === 'error' ? ' There was an error Processing the video.' :
                 ' This video needs status verification.'}
                <button
                  type="button"
                  className={`btn btn-sm ms-3 ${statusCheckResult?.status === 'ready' ? 'btn-success' : statusCheckResult?.status === 'error' ? 'btn-danger' : statusCheckResult?.status === 'Queued' ? 'btn-warning' : 'btn-primary'}`}
                  onClick={handleStatusButtonClick}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </span>
                      Checking...
                    </>
                  ) : statusCheckResult?.status === 'ready' ? (
                    "Video Ready"
                  ) : statusCheckResult?.status === 'Processing' || statusCheckResult?.status === 'error' ? (
                    "Try Again"
                  ) : statusCheckResult?.status === 'Queued' ? (
                    "Queued"
                  ) : (
                    "Status"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Video Player - Show when video is ready */}
        {isVideoReady && (
          <div className="row mt-3">
            <div className="col-12">
              <div className="alert alert-success" role="alert">
                <strong>Video Ready!</strong> Your video is ready to play.
              </div>
              <div className="video-player-container">
                <iframe
                  src={`https://player.vdocipher.com/v2/?otp=${videoDetails.video_otp}&playbackInfo=${videoDetails.video_playback_info}`}
                  width="100%"
                  height="400"
                  frameBorder="0"
                  allowFullScreen
                  title="Video Player"
                  allow="encrypted-media"
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
                {/* DRM Error Help */}
               
              </div>
            </div>
          </div>
        )}
        
        {isLoading && (
          <div className="text-center">
            <div className="spinner-border spinner-border-sm me-2" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            Uploading video...
          </div>
        )}
      </form>
    </div>
  );
});

export default VideoForm; 